import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { LOGIN } from '@/shared/constants/path';
import { toast } from '@/shared/hooks/useToast';
import { ERROR_TITLE, ERROR_DESCRIPTION } from '@/shared/constants/error';
import { BASE_PATH } from '../constants/constant';
import useStore from '@/shared/store/useStore';

// Axios config function to avoid redundancy
const createAxiosInstance = () => {
    const instance = axios.create({
        timeout: 30000,
        baseURL: `${BASE_PATH}/api`, // Point to Next.js API routes (BFF)
        withCredentials: true, // Ensure cookies are sent
        headers: {
            'Content-Type': 'application/json',
            'X-CLIENT-TYPE': 'WEB_CMS',
            'X-CLIENT-VERSION': '20241118.093542'
        }
    });

    instance.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            // Attach CSRF token from cookie (non-HttpOnly, readable by JS)
            const csrfToken = Cookies.get('csrf_token');
            if (csrfToken) {
                config.headers['X-CSRF-Token'] = csrfToken;
            }
            return config;
        },
        (error: AxiosError) => Promise.reject(error)
    );

    instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (err: AxiosError<{ meta?: { message?: string }; message?: string }>) => {
            // Only log non-401 errors (401 is expected on login page / session expiry)
            if (err.response?.status !== 401) {
                console.warn('error response', err);
            }

            let isRedirecting = false;
            
            if (err.response?.status === 401) {
                // Always clear auth state on 401 to prevent stale localStorage
                await useStore.getState().logout();

                // Only redirect to login if not already there (avoid loops)
                if (!isRedirecting && typeof window !== 'undefined' && !window.location.pathname.includes(LOGIN)) {
                    isRedirecting = true;
                    
                    window.location.href = `${BASE_PATH}${LOGIN}`;
                    toast({
                        variant: 'destructive',
                        title: 'Session Anda Telah Berakhir',
                        description: 'Silakan login kembali.',
                    });
                }
            } else {
                console.error('Error response:', err);
                toast({
                    variant: 'destructive',
                    title: ERROR_TITLE[err?.response?.status as keyof typeof ERROR_TITLE] ?? 'Network Error',
                    description: err?.response?.data?.meta?.message ?? err?.response?.data?.message ?? ERROR_DESCRIPTION[err?.response?.status as keyof typeof ERROR_DESCRIPTION] ?? err?.message
                });
            }

            
            return Promise.reject(err);
        }
    );

    return instance;
};

// Create separate Axios instances - both point to the same BFF proxy
export const AuthAxios = createAxiosInstance();
export const ResourceAxios = createAxiosInstance();
